import { useState } from "react";
import { useQuery } from "@apollo/client";
import { gql } from "../../../__generated__";
import clsx from "clsx";
import type { StoreReserveTimeListQuery } from "../../../__generated__/graphql";
import ReserveCalendar from "./reserve-calendar";
import { AdminListDetailLayout } from "../components/admin-list-detail-layout";
import { AdminStoreTableWithMobileCards } from "../components/admin-store-table-with-mobile-cards";
import { useScrollToDetailPanelOnSelect } from "../hooks/use-scroll-to-detail-panel";

const STORE_RESERVE_TIME_LIST = gql(`
  query StoreReserveTimeList {
    getStoreList {
      ok
      error
      result {
        id
        title
        img
        address
        reserveTime
        pack {
          isTomorrowReservationEnabled
        }
      }
    }
  }
`);

const daysOfWeek: number[] = [0, 1, 2, 3, 4, 5, 6];
const dayLabels: string[] = ["월", "화", "수", "목", "금", "토", "일"];

type StoreRow = NonNullable<
  StoreReserveTimeListQuery["getStoreList"]
>["result"][number];

const AdminStoreReserveTime = () => {
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedStore, setSelectedStore] = useState<StoreRow | null>(null);

  useScrollToDetailPanelOnSelect(selectedStore);

  const { data, loading, error } = useQuery<StoreReserveTimeListQuery>(
    STORE_RESERVE_TIME_LIST,
  );

  const fullList = data?.getStoreList?.ok
    ? (data.getStoreList.result ?? [])
    : [];
  const keyword = searchKeyword.trim().toLowerCase();
  const list = keyword
    ? fullList.filter(
        (store: StoreRow) =>
          store.title.toLowerCase().includes(keyword) ||
          (store.address?.toLowerCase().includes(keyword) ?? false),
      )
    : fullList;

  if (loading) {
    return (
      <div className="w-full p-4">
        <div className="text-gray-500">로딩 중...</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="w-full p-4">
        <div className="text-red-600">에러: {error.message}</div>
      </div>
    );
  }
  if (!data?.getStoreList?.ok) {
    return (
      <div className="w-full p-4">
        <div className="text-red-600">
          {data?.getStoreList?.error ?? "목록을 불러올 수 없습니다."}
        </div>
      </div>
    );
  }

  return (
    <AdminListDetailLayout
      title="매장 예약 시간"
      description="매장을 선택한 뒤 예약 시간을 확인·수정하세요."
      leftPanel={{
        title: "매장 목록",
        searchPlaceholder: "매장명·주소로 검색",
        searchValue: searchKeyword,
        onSearchChange: setSearchKeyword,
        children: (
          <AdminStoreTableWithMobileCards
            stores={list.map((s) => ({
              id: s.id,
              title: s.title,
              img: s.img,
              address: s.address ?? undefined,
            }))}
            selectionMode="single"
            selectedIds={selectedStore ? [selectedStore.id] : []}
            onSelect={(store) =>
              setSelectedStore(list.find((x) => x.id === store.id) ?? null)
            }
            emptyText={
              keyword
                ? "검색 결과가 없습니다."
                : "등록된 매장이 없습니다."
            }
          />
        ),
      }}
      rightPanel={{
        title: "예약 시간 설정",
        children: selectedStore ? (
          <div className="space-y-4">
            <div className="text-sm text-gray-700">
              <span className="font-medium">선택 매장:</span>{" "}
              {selectedStore.title} (ID: {selectedStore.id})
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">
                내일 예약
              </h3>
              <p
                className={clsx(
                  "text-lg font-bold",
                  selectedStore.pack?.isTomorrowReservationEnabled
                    ? "text-green-500"
                    : "text-red-500",
                )}
              >
                {selectedStore.pack?.isTomorrowReservationEnabled
                  ? "ON"
                  : "OFF"}
              </p>
            </div>

            <div className="bg-gray-50 p-3 rounded-md">
              <h3 className="text-sm font-medium text-gray-700 mb-2">
                요일별 예약 시간
              </h3>
              {selectedStore.reserveTime &&
              Object.keys(selectedStore.reserveTime).length > 0 ? (
                <div className="space-y-1 text-sm">
                  {daysOfWeek.map((day) => (
                    <div key={day} className="flex justify-between py-1">
                      <div className="flex items-center gap-2">
                        <div
                          className={clsx(
                            "w-4 h-4 rounded-full",
                            selectedStore.reserveTime?.[day]?.enabled
                              ? "bg-green-500"
                              : "bg-gray-300",
                          )}
                        />
                        <span>{dayLabels[day]}:</span>
                      </div>
                      <span className="text-gray-600">
                        {selectedStore.reserveTime?.[day]?.start ?? "시작 없음"}{" "}
                        ~ {selectedStore.reserveTime?.[day]?.end ?? "종료 없음"}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-gray-500 text-sm">
                  예약 시간이 없습니다.
                </div>
              )}
            </div>

            <ReserveCalendar
              store={{
                id: selectedStore.id,
                reserveTime: selectedStore.reserveTime,
              }}
            />
          </div>
        ) : null,
      }}
    />
  );
};

export default AdminStoreReserveTime;
